import {DataPage, assertPartialSchema, createAction, useSelector, v} from '../../../lib'
import {ProjectDataDownloadFlowMetadata} from '../../../model'
import {IdentityType} from '../../../shared/db'

export enum DataDownloadFlowMetadataActionType {
  DATA_DOWNLOAD_FLOW_METADATA_LIST_SET = 'DATA_DOWNLOAD_FLOW_METADATA_LIST_SET',
  DATA_DOWNLOAD_FLOW_METADATA_UPSERT = 'DATA_DOWNLOAD_FLOW_METADATA_UPSERT',
}

export const doDATA_DOWNLOAD_FLOW_METADATA_LIST_SET = createAction(
  DataDownloadFlowMetadataActionType.DATA_DOWNLOAD_FLOW_METADATA_LIST_SET,
)
export const doDATA_DOWNLOAD_FLOW_METADATA_UPSERT = createAction(
  DataDownloadFlowMetadataActionType.DATA_DOWNLOAD_FLOW_METADATA_UPSERT,
)

export const dataDownloadFlowMetadataActionCreators = {
  doDATA_DOWNLOAD_FLOW_METADATA_LIST_SET,
  doDATA_DOWNLOAD_FLOW_METADATA_UPSERT,
}

export interface DataDownloadFlowMetadataProjectState {
  byId: Record<string, ProjectDataDownloadFlowMetadata>
  orderedIds: string[]
  page: DataPage | null
}

export interface DataDownloadFlowMetadataState {
  [projectId: string]: DataDownloadFlowMetadataProjectState
}

interface RootState {
  dataDownloadFlowMetadata: DataDownloadFlowMetadataState
}

export const selectDataDownloadFlowMetadata = () => {
  return useSelector((state: RootState) => state.dataDownloadFlowMetadata ?? dataDownloadFlowMetadataDefaultState)
}

export const dataDownloadFlowMetadataDefaultState: DataDownloadFlowMetadataState = {}

type Action =
  | {
      type: DataDownloadFlowMetadataActionType.DATA_DOWNLOAD_FLOW_METADATA_LIST_SET
      payload: {
        projectId: string
        items: ProjectDataDownloadFlowMetadata[]
        page: DataPage
      }
    }
  | {
      type: DataDownloadFlowMetadataActionType.DATA_DOWNLOAD_FLOW_METADATA_UPSERT
      payload: ProjectDataDownloadFlowMetadata
    }

const metadataSchema = v.object<ProjectDataDownloadFlowMetadata>({
  id: v.string().exist(),
  createdAt: v.date().exist(),
  registeredAt: v.date().optional().allow(null),
  executionStart: v.date().exist(),
  projectId: v.string().uuid().exist(),
  input: v
    .object({
      workspaceId: v.string().uuid().exist(),
      projectId: v.string().uuid().exist(),
      participantIds: v.array().items(v.string().uuid()).exist(),
      startDateIso: v.string().optional(),
      endDateIso: v.string().optional(),
    })
    .exist(),
  inputHash: v.string().exist(),
  osStats: v.object().optional(),
  success: v.boolean().exist(),
  failure: v
    .object({
      name: v.string().exist(),
      message: v.string().exist(),
      stack: v.array().items(v.string()).exist(),
    })
    .optional(),
  url: v.alternatives(v.string(), v.array().items(v.string().exist())).optional(),
  isExpired: v.boolean().optional(),
  expireAt: v.date().optional().allow(null),
  downloadedAt: v.date().optional().allow(null),
  compressedSizeInBytes: v.number().optional().allow(null),
  uncompressedSizeInBytes: v.number().optional().allow(null),
  partsBreakdown: v
    .array()
    .items(
      v.object({
        partIndex: v.number().exist(),
        key: v.string().exist(),
        size: v.number().exist(),
        sizeUncompressed: v.number().exist(),
        participants: v
          .array()
          .items(
            v.object({
              id: v.string().exist(),
              sizeUncompressed: v.number().exist(),
            }),
          )
          .exist(),
      }),
    )
    .optional(),
  requester: v
    .object({
      id: v.string().exist(),
      type: v
        .string()
        .valid(...Object.values(IdentityType))
        .exist(),
      email: v.string().exist(),
    })
    .optional(),
})

const pageSchema = v.object<DataPage>({
  total: v.number().exist(),
  size: v.number().exist(),
  current: v.number().exist(),
  next: v.number().allow(null).exist(),
  last: v.number().exist(),
})

const createEmptyProjectState = (): DataDownloadFlowMetadataProjectState => ({
  byId: {},
  orderedIds: [],
  page: null,
})

const mergeMetadataList = (
  currentState: DataDownloadFlowMetadataProjectState | undefined,
  items: ProjectDataDownloadFlowMetadata[],
  page: DataPage,
): DataDownloadFlowMetadataProjectState => {
  const nextState = page.current === 0 ? createEmptyProjectState() : (currentState ?? createEmptyProjectState())
  const byId = {...nextState.byId}
  const orderedIds = [...nextState.orderedIds]

  for (const item of items) {
    byId[item.id] = item
    if (!orderedIds.includes(item.id)) {
      orderedIds.push(item.id)
    }
  }

  return {
    byId,
    orderedIds,
    page,
  }
}

export const dataDownloadFlowMetadataReducer = (
  state = dataDownloadFlowMetadataDefaultState,
  {type, payload}: Action,
): DataDownloadFlowMetadataState => {
  switch (type) {
    case DataDownloadFlowMetadataActionType.DATA_DOWNLOAD_FLOW_METADATA_LIST_SET: {
      assertPartialSchema({
        payload,
        schema: v.object({
          projectId: v.string().uuid().exist(),
          items: v.array().items(metadataSchema).exist(),
          page: pageSchema.exist(),
        }),
      })
      return {
        ...state,
        [payload.projectId]: mergeMetadataList(state[payload.projectId], payload.items, payload.page),
      }
    }
    case DataDownloadFlowMetadataActionType.DATA_DOWNLOAD_FLOW_METADATA_UPSERT: {
      assertPartialSchema({
        payload,
        schema: metadataSchema,
      })
      const currentState = state[payload.projectId] ?? createEmptyProjectState()
      return {
        ...state,
        [payload.projectId]: {
          ...currentState,
          byId: {
            ...currentState.byId,
            [payload.id]: payload,
          },
          orderedIds: currentState.orderedIds.includes(payload.id)
            ? currentState.orderedIds
            : [payload.id, ...currentState.orderedIds],
        },
      }
    }
    default:
      return state
  }
}
